import { useEffect, useState } from 'react'

import { ImageOff } from 'lucide-react'

import { ImagePreview, type GalleryItem } from '@/shared/components/ui'

import { SEV_COLOR } from './IssuesListRow'

import type { GisIssueDetail } from '../../../api/types'

/** Every photo tied to the issue: its own attachment first, then each timeline event's photos,
 *  de-duplicated (an event often echoes the raise photo) and dropping any empty urls. */
function collectPhotos(detail: GisIssueDetail): string[] {
  const raw = [detail.photoUrl, ...detail.events.flatMap((e) => e.photoUrls)]
  return [...new Set(raw.filter((u): u is string => Boolean(u)))]
}

/** Placeholder shown when the issue has no photos — a severity-tinted card that matches a real hero's
 *  footprint (bounded 4:3, never a full-height block), so an empty issue's rail stays compact. */
function EmptyGallery({ severity }: { severity: GisIssueDetail['severity'] }) {
  return (
    <div
      className="mx-auto grid aspect-[4/3] w-full max-w-[483px] place-items-center rounded-2xl text-white/80"
      style={{ background: `linear-gradient(150deg, ${SEV_COLOR[severity]}, #16243f)` }}
    >
      <div className="flex flex-col items-center gap-2">
        <ImageOff className="h-8 w-8 stroke-[1.6]" />
        <span className="text-[12px] font-semibold">No photos for this issue</span>
      </div>
    </div>
  )
}

/**
 * Left column of the gallery modal: a hero image with an inline prev/next carousel and a thumbnail
 * strip. The hero reuses the shared `ImagePreview` so a click opens the full zoom lightbox (its own
 * arrows + counter). Combines the issue's own photo with every event photo (deduped).
 */
export function IssueModalGallery({ detail }: { detail: GisIssueDetail }) {
  const photos = collectPhotos(detail)
  const [heroIndex, setHeroIndex] = useState(0)

  // A fresh issue (header ‹ ›) resets the carousel to its first photo.
  useEffect(() => {
    setHeroIndex(0)
  }, [detail.id])

  if (photos.length === 0) {
    return <EmptyGallery severity={detail.severity} />
  }

  const items: GalleryItem[] = photos.map((src) => ({ src, alt: detail.title }))
  const total = photos.length
  const safeIndex = heroIndex % total

  return (
    <div className="mx-auto flex w-full max-w-[483px] flex-col gap-3">
      <div className="relative w-full overflow-hidden rounded-2xl bg-gis-track">
        <ImagePreview
          key={photos[safeIndex]}
          src={photos[safeIndex]}
          alt={detail.title}
          gallery={items}
          galleryIndex={safeIndex}
          className="aspect-[4/3] w-full object-cover"
        />
      </div>

      <div className="flex items-center justify-between px-0.5">
        <span className="text-[10.5px] font-bold tracking-[.08em] text-gis-heading uppercase">
          Images ({total})
        </span>
        <span className="text-[11px] font-semibold tabular-nums text-gis-soft">
          {safeIndex + 1} / {total}
        </span>
      </div>

      {total > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
          {photos.map((src, i) => (
            <button
              key={src}
              type="button"
              onClick={() => setHeroIndex(i)}
              aria-label={`Show image ${i + 1}`}
              aria-current={i === safeIndex}
              className={`h-14 w-14 flex-none overflow-hidden rounded-[10px] border transition focus-visible:ring-2 focus-visible:ring-blue-500/60 ${
                i === safeIndex ? 'border-blue-500 ring-2 ring-blue-500/40' : 'border-gis-line hover:border-gis-line-2'
              }`}
            >
              <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
